import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import cogoToast from 'cogo-toast';
import { createPaymentOrder, queryOrderStatus } from '../../utils/opayUtils';

// Generate a unique order number
const generateOrderNumber = () => {
  return `ORDER_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
};

// Async thunk for initiating OPay payment
export const initiateOpayPayment = createAsyncThunk(
  'payment/initiateOpayPayment',
  async (paymentData, { rejectWithValue }) => {
    try {
      const orderNumber = paymentData.orderNumber || generateOrderNumber();
      const orderData = {
        orderNumber,
        amount: paymentData.amount,
        orderDetails: paymentData.orderDetails || {
          items: paymentData.items || [],
        },
        userInfo: paymentData.userInfo || {},
      };

      const response = await createPaymentOrder(orderData);

      if (response.success) {
        return {
          orderNumber,
          opayOrderNo: response.data.orderNo,
          reference: response.data.outOrderNo,
          cashierUrl: response.data.cashierUrl,
          status: response.data.status,
          amount: response.data.amount,
        };
      } else {
        return rejectWithValue(response.error || 'Failed to initiate payment');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Unknown error occurred');
    }
  }
);

// Async thunk for checking payment status
export const checkPaymentStatus = createAsyncThunk(
  'payment/checkPaymentStatus',
  async ({ orderNo, reference }, { rejectWithValue }) => {
    try {
      const response = await queryOrderStatus({ orderNo, reference });

      if (response.success) {
        return response.data;
      } else {
        return rejectWithValue(
          response.error || 'Failed to check payment status'
        );
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Unknown error occurred');
    }
  }
);

const paymentSlice = createSlice({
  name: 'payment',
  initialState: {
    loading: false,
    paymentInitiated: false,
    paymentComplete: false,
    paymentCancelled: false,
    paymentData: null,
    orderStatus: null,
    error: null,
  },
  reducers: {
    resetPayment(state) {
      state.loading = false;
      state.paymentInitiated = false;
      state.paymentComplete = false;
      state.paymentCancelled = false;
      state.paymentData = null;
      state.orderStatus = null;
      state.error = null;
    },
    setPaymentComplete(state, action) {
      state.paymentComplete = true;
      state.orderStatus = action.payload || 'SUCCESS';
    },
    setPaymentCancelled(state) {
      state.paymentCancelled = true;
      state.paymentComplete = false;
      state.orderStatus = 'CANCELLED';
    },
  },
  extraReducers: (builder) => {
    // Handle initiateOpayPayment
    builder.addCase(initiateOpayPayment.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(initiateOpayPayment.fulfilled, (state, action) => {
      state.loading = false;
      state.paymentInitiated = true;
      state.paymentData = action.payload;
      cogoToast.success('Payment initiated successfully', {
        position: 'bottom-left',
      });
    });
    builder.addCase(initiateOpayPayment.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      cogoToast.error(`Payment initiation failed: ${action.payload}`, {
        position: 'bottom-left',
      });
    });

    // Handle checkPaymentStatus
    builder.addCase(checkPaymentStatus.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(checkPaymentStatus.fulfilled, (state, action) => {
      state.loading = false;
      state.orderStatus = action.payload.status;

      if (action.payload.status === 'SUCCESS') {
        state.paymentComplete = true;
        cogoToast.success('Payment completed successfully', {
          position: 'bottom-left',
        });
      } else if (action.payload.status === 'PENDING') {
        cogoToast.info('Payment is pending', { position: 'bottom-left' });
      } else if (
        action.payload.status === 'FAIL' ||
        action.payload.status === 'CLOSE' ||
        action.payload.status === 'CANCEL'
      ) {
        state.paymentComplete = false;
        state.paymentCancelled = true;
        cogoToast.error(`Payment failed: ${action.payload.status}`, {
          position: 'bottom-left',
        });
      }
    });
    builder.addCase(checkPaymentStatus.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      cogoToast.error(`Failed to check payment status: ${action.payload}`, {
        position: 'bottom-left',
      });
    });
  },
});

export const { resetPayment, setPaymentComplete, setPaymentCancelled } =
  paymentSlice.actions;

export default paymentSlice.reducer;
